/** @jsxImportSource @emotion/react */
import { useSortable } from "@dnd-kit/sortable";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import { FaRegCalendarAlt, FaRegTrashAlt } from "react-icons/fa";
import useClickOutside from "../../../../../../hook/useClickOutside";
import { Flex } from "../../../../../../styles/general";
import StyledTimePicker from "../../../../../StyledTimePicker";

const TimePickerContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 4px;
  height: 250px !important;
  position: absolute;
  right: 0;
  z-index: 99;
`;

interface CardProps {
  isDisabled?: boolean;
  isView?: boolean;
}

const Card = styled.div<CardProps>`
  border: 1px solid #dddddd;
  padding: 10px;
  border-radius: 5px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${(props) =>
    props.isDisabled ? "not-allowed" : props.isView ? "pointer" : "default"};
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};

  :hover {
    opacity: ${(props) =>
      props.isDisabled && props.isView
        ? 0.6
        : !props.isDisabled && props.isView
        ? 0.8
        : 1};
  }
`;

const Title = styled.div`
  text-align: center;
  font: 14px;
  color: #2f2f2f;
  cursor: pointer;
`;

const Button = styled.div`
  cursor: pointer;
  width: 46px;
  height: 20px;
  position: relative;
`;

const IconAndTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TimeCardWrapper = styled(motion.li)`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: #fff;
`;

interface Props {
  id: string;
  time: string;
  edit: (date: string) => void;
  delete: () => void;
}

const initialStyles = {
  x: 0,
  y: 0,
  scale: 1,
};

export default function TimeCard(props: Props): JSX.Element {
  const [timeLabel, setTimeLabel] = useState("Choose Time");
  const [step, setStep] = useState<"hour" | "minute">("hour");
  const [isTimePickerActive, setIsTimePickerActive] = useState(false);
  const [newHour, setNewHour] = useState<Date>(dayjs(props.time).toDate());
  const wrapperRef = useClickOutside(() => setIsTimePickerActive(false));

  const { attributes, setNodeRef, listeners, transform, isDragging } =
    useSortable({
      id: props.id,
      transition: null,
    });

  function handleShowTimePicker() {
    setIsTimePickerActive(!isTimePickerActive);
  }

  useEffect(() => {
    if (newHour && newHour.getMinutes() !== 21) {
      setTimeLabel(
        `${dayjs(newHour).format("LT").toLowerCase()} ${dayjs(newHour).format(
          "(z)"
        )}`
      );
    } else {
      setTimeLabel("Choose Time");
    }
  }, [newHour]);

  // const isDisabled = useCallback(() => {
  //   const hour = dayjs(props.time).get("hours");
  //   const minute = dayjs(props.time).get("minute");

  //   const isBefore = dayjs()
  //     .set("hours", hour)
  //     .set("minutes", minute)
  //     .isBefore(dayjs());

  //   return isBefore;
  // }, [props.time]);

  /**
   * Draggin with popover usually have issues
   * as the popover appears below the draggable components.
   * For that reason, whenever a popover is spawner, I'll take its parent
   * component and give it a bigger z-index
   *
   * - Palhari
   */
  const draggableContainerRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    setImmediate(() => {
      if (!draggableContainerRef.current) return;

      if (isTimePickerActive) {
        draggableContainerRef.current.style.zIndex = "1";
      } else {
        draggableContainerRef.current.style.zIndex = "auto";
      }
    });
  }, [isTimePickerActive]);

  useEffect(() => {
    if (isDragging) {
      setIsTimePickerActive(false);
    }
  }, [isDragging]);

  const Container = useMemo(() => {
    return (
      <Card key={`time_card_${props.id}`} ref={wrapperRef}>
        <IconAndTitleContainer>
          <AiOutlineMenu
            {...listeners}
            css={css`
              cursor: grabbing;
              margin-right: 15px;
            `}
          />
          <Title onClick={() => handleShowTimePicker()}>{timeLabel}</Title>
        </IconAndTitleContainer>

        <Button>
          <Flex
            css={css`
              height: 100%;
              width: 100%;
            `}
          >
            <FaRegTrashAlt
              onClick={() => props.delete()}
              color={"#707070"}
              size={17}
              css={css`
                margin-right: 10px;
              `}
            />

            <FaRegCalendarAlt
              onClick={() => handleShowTimePicker()}
              color={"#707070"}
              size={20}
            />
          </Flex>

          {isTimePickerActive && (
            <TimePickerContainer>
              <StyledTimePicker
                step={step}
                setStep={setStep}
                goBack={() => {
                  handleShowTimePicker();
                  setStep("hour");
                }}
                favoriteTime
                date={newHour}
                minuteInterval={5}
                onSelect={async ({ hour, minute }) => {
                  let selectedDateTime = dayjs();
                  selectedDateTime = selectedDateTime.set(
                    "hours",
                    hour as number
                  );
                  selectedDateTime = selectedDateTime.set(
                    "minutes",
                    minute as number
                  );
                  selectedDateTime = selectedDateTime.set("seconds", 0);
                  setNewHour(selectedDateTime.toDate());
                  props.edit(selectedDateTime.toString());
                  handleShowTimePicker();
                  setStep("hour");
                }}
              />
            </TimePickerContainer>
          )}
        </Button>
      </Card>
    );
  }, [isTimePickerActive, step, newHour, timeLabel]);

  return (
    <TimeCardWrapper
      ref={(r) => {
        draggableContainerRef.current = r;
        setNodeRef(r);
      }}
      {...attributes}
      tabIndex={0}
      layoutId={props.id}
      animate={
        transform
          ? {
              x: transform.x,
              y: transform.y,
              scale: isDragging ? 1.05 : 1,
              zIndex: isDragging ? 1 : 0,
              boxShadow: isDragging
                ? "0 0 0 1px rgba(63, 63, 68, 0.05), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)"
                : undefined,
            }
          : initialStyles
      }
      transition={{
        duration: !isDragging ? 0.25 : 0,
        easings: {
          type: "spring",
        },
        scale: {
          duration: 0.25,
        },
        zIndex: {
          delay: isDragging ? 0 : 0.25,
        },
      }}
    >
      {Container}
    </TimeCardWrapper>
  );
}
